import { createReducer } from '@reduxjs/toolkit';
import { ArticleDto, IArticleDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyArticle,
    clearArticle,
    saveArticle,
    importArticle,
    updateArticleTitle,
    updateArticleDescription,
    createNewArticle,
    updateArticleInnerCode,
    updateArticleOutterCode,
    updateArticleManufacturer,
    updateArticleDimensionType,
    setResponseMessage,
    removeLabelFromArticle,
} from '../actions/articleActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface ArticleReducerState extends IBaseEntityState {
    article: IArticleDto | null;
    responseMessage: string | null;
}

function clearState(state: ArticleReducerState): void {
    state.article = null;
    state.responseMessage = null;

    clearBaseEntityState(state);
}

function getInitialState(): ArticleReducerState {
    const state: ArticleReducerState = {
        article: null,
        responseMessage: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: ArticleReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.article != null, 'Need article to calculate');

    const jsonBaseline = JSON.stringify(state.article);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: ArticleReducerState) {
    myAssert(() => state.article != null, 'Need article to calculate');

    if (!state.article!.id || state.article!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of article
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.article, undefined);

        state.modified =
            (modified ?? state.article!.created)
                ? { modifiedAt: state.article!.created ?? new Date(), modifiedBy: state.article!.createdBy ?? '' }
                : undefined;
    }
}

export const articleReducer = createReducer<ArticleReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyArticle, (state: ArticleReducerState, action: any) => {
                clearState(state);

                const newArticle: IArticleDto = new ArticleDto();

                newArticle.id = 0;
                newArticle.title = undefined;
                newArticle.description = undefined;
                newArticle.innerCode = undefined;
                newArticle.outterCode = undefined;
                newArticle.calculatedAveragePrice = undefined;

                state.article = newArticle;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewArticle, (state: ArticleReducerState, action: any) => {
                clearState(state);

                const payload: IArticleDto = action.payload;

                // check article data
                myAssert(() => payload.id === 0, 'It is new article!!!');

                state.article = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importArticle.fulfilled, (state: ArticleReducerState, action: any) => {
                state.article = action.payload ?? new ArticleDto();
            })
            .addCase(clearArticle, (state: ArticleReducerState, action: any) => {
                clearState(state);
            })
            .addCase(updateArticleTitle, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if it is being updated');

                state.article! = { ...state.article, title: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateArticleManufacturer, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if it is being updated');

                state.article! = { ...state.article, manufacturer: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateArticleDimensionType, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if it is being updated');

                state.article! = { ...state.article, dimensionType: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateArticleDescription, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if it is being updated');

                state.article! = { ...state.article, description: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateArticleInnerCode, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if it is being updated');

                state.article! = { ...state.article, innerCode: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateArticleOutterCode, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if it is being updated');

                state.article! = { ...state.article, outterCode: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(removeLabelFromArticle, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if it is being updated');

                const labelId: number = action.payload;

                const labels = state.article?.labels;

                if (!!labels) {
                    const labelIndex = labels.findIndex((l) => l.id === labelId);
                    if (labelIndex >= 0) {
                        labels.splice(labelIndex, 1);

                        state.article! = { ...state.article, labels: labels };
                    }
                }
                calculateCurrentJson(state);
            })
            .addCase(saveArticle.pending, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveArticle.fulfilled, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveArticle.rejected, (state: ArticleReducerState, action: any) => {
                myAssert(() => state.article !== null, 'There needs to be article if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(setResponseMessage, (state: ArticleReducerState, action: any) => {
                state.responseMessage = action.payload;
            });
    },
);
